import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { actions as offerReduxActionTypes, utils as offerReduxUtils } from '@makemydeal/dr-offer-redux';
import type { OfferType } from '@makemydeal/dr-platform-types';
import type { DashMiddleware, DashNext, DashStore } from '@makemydeal/dr-shared-store';
import { initActionTypes } from '@makemydeal/dr-shared-store';
import type { StateTree } from '@makemydeal/dr-dash-types';

import { PAYMENT_RECEIVED } from '../actionTypes/deskingActionTypes';
import { clearProgramAutoSelected, markProgramAutoSelected } from '../actions/programActionCreators';
import { CALC_ENDPOINTS, OPEN_DEAL_APPLICATION_NAME } from '../constants';
import { KEEP_LENDER_META } from '../programInvalidatingActions';
import * as dealTemplateSelectors from '../selectors/dealTemplateSelectors';
import * as firstPencilSelectors from '../selectors/firstPencilSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';
import { getApplicationName } from '../selectors/offerSelectors';
import * as paymentSelectors from '../selectors/payment';
import { autoSelectedBannerAppliesToOffer, getExplicitDealProgram, getPrograms } from '../selectors/programsSelectors';
import { describesSameLenderProgram } from '../utils/programUtils';

const { INIT_SUCCESS } = initActionTypes;

const pricedOfferType = (action: AnyFSA, state: StateTree): OfferType =>
    action.meta?.originalAction?.meta?.middleware?.offerType ||
    action.meta?.offerType ||
    offerReduxSelectors.getCurrentOfferType(state);

const recordableTermProgram = (state: StateTree, offerType: OfferType) => {
    const lenderProgram = offerReduxSelectors.getSelectedTermByOfferType(state, offerType)?.lenderProgram;
    return lenderProgram?.programName && !offerReduxUtils.isUserPricedProgram(lenderProgram) ? lenderProgram : undefined;
};

const programsTheDealHad = (state: StateTree, offerType: OfferType): Array<Record<string, any> | undefined> => [
    getExplicitDealProgram(state, offerType),
    offerReduxSelectors.getDealPaymentProgram(state)?.lenderProgram
];

const isFreshOpenDealPricedByAcceleron = (state: StateTree): boolean =>
    getApplicationName(state).trim().toLowerCase() === OPEN_DEAL_APPLICATION_NAME &&
    paymentSelectors.getPaymentsCalculated(state) &&
    !firstPencilSelectors.hasAutoSelectProgramSaved(state) &&
    !dealTemplateSelectors.getIsDealTemplateApplied(state);

const markProgramPickedOnLoad = (store: DashStore): void => {
    const state = store.getState();
    const offerType = offerReduxSelectors.getCurrentOfferType(state);
    if (!autoSelectedBannerAppliesToOffer(state, offerType)) return;
    if (!offerReduxSelectors.getIsProgramQuotes(state, offerType)) return;
    if (!isFreshOpenDealPricedByAcceleron(state)) return;

    const lenderProgram = recordableTermProgram(state, offerType);
    if (lenderProgram) store.dispatch(markProgramAutoSelected(offerType, lenderProgram));
};

const markProgramPickedByProgramQuotes = (
    store: DashStore,
    action: AnyFSA,
    offerType: OfferType,
    programsBefore: Array<Record<string, any> | undefined>
): void => {
    if (action.meta?.originalAction?.type === offerReduxActionTypes.APPLIED_PROGRAM) return;
    if (action.meta?.originalAction?.meta?.[KEEP_LENDER_META]) return;
    if (action.meta?.requestCalculationEndpoint !== CALC_ENDPOINTS.programQuotes) return;

    const lenderProgram = recordableTermProgram(store.getState(), offerType);
    if (!lenderProgram) return;
    if (programsBefore.some((programBefore) => describesSameLenderProgram(lenderProgram, programBefore))) return;

    store.dispatch(markProgramAutoSelected(offerType, lenderProgram));
};

const forgetProgramPickedInProgramsTable = (store: DashStore, action: AnyFSA, offerType: OfferType): void => {
    if (!action.meta?.originalAction?.meta?.programsTableSelect) return;
    if (!getPrograms(store.getState()).autoSelectedProgramByOfferType?.[offerType]) return;

    store.dispatch(clearProgramAutoSelected(offerType));
};

export const middleware: DashMiddleware = (store: DashStore) => (next: DashNext) => (action: AnyFSA) => {
    if (action.type !== PAYMENT_RECEIVED) {
        next(action);
        if (action.type === INIT_SUCCESS) markProgramPickedOnLoad(store);
        return;
    }

    const stateBefore = store.getState();
    const offerType = pricedOfferType(action, stateBefore);
    if (!autoSelectedBannerAppliesToOffer(stateBefore, offerType)) {
        next(action);
        return;
    }
    const programsBefore = programsTheDealHad(stateBefore, offerType);

    next(action);

    if (action.meta?.isStalePaymentResponse) return;

    forgetProgramPickedInProgramsTable(store, action, offerType);
    markProgramPickedByProgramQuotes(store, action, offerType, programsBefore);
};
